import { FC, FormEvent, useState } from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { Maybe, User, UserProfile } from '../../../types/graphql';
import { FillButton, GhostButton } from '../../atoms/button/Button';
import { Input } from '../../atoms/controls/Input';
import { Icon } from '../../atoms/icon/Icon';
import { Card } from '../../molecules/card/Card';
import { Author } from './Author';

export type AuthorUser = Maybe<
  Pick<User, 'id'> & {
    profile: Maybe<Pick<UserProfile, 'avatar' | 'display_name' | 'slug'>>;
  }
>;

export interface AuthorsProps {
  authors: (AuthorUser & { loading?: boolean })[];
  onAddAuthor?: (author: AuthorUser) => void;
  onRemoveAuthor?: (author: AuthorUser) => void;
  onSearch?: (event: FormEvent) => void;
  search?: string;
  isSearching?: boolean;
  errorMessage?: string;
  users?: AuthorUser[];
}

const AutoCompleteCard = styled(Card)`
  ${tw`absolute w-full p-2 shadow-card flex flex-col space-y-1`}
`;

export const Authors: FC<AuthorsProps> = ({
  authors,
  onAddAuthor,
  onRemoveAuthor,
  onSearch,
  search = '',
  isSearching,
  errorMessage,
  users,
  ...props
}) => {
  const [editing, setEditing] = useState(false);

  return (
    <div {...props}>
      {authors.map(
        author =>
          author.profile && (
            <Author
              loading={author.loading}
              profile={author.profile}
              key={author.id}
              onRemove={onRemoveAuthor ? () => onRemoveAuthor(author) : undefined}
              tw="mb-2"
            />
          ),
      )}
      {onAddAuthor && !editing && (
        <GhostButton onClick={() => setEditing(true)} tw="mt-1">
          <Icon name="add-user" tw="mr-1" /> Add author
        </GhostButton>
      )}
      {onAddAuthor && editing && (
        <div tw="relative">
          <Input
            autoFocus
            error={search.length > 0 ? errorMessage : undefined}
            touched
            label="Author"
            onChange={onSearch}
            placeholder="Search for name, username, or email"
            value={search}
            tw="mb-1"
          />
          {isSearching && (
            <AutoCompleteCard tw="justify-center items-center h-16">
              <Icon name="circle-loader" tw="animate-spin text-xl" role="alert" />
            </AutoCompleteCard>
          )}
          {search.length > 0 && users && users.length > 0 && (
            <AutoCompleteCard>
              {users.map(
                user =>
                  user?.profile && (
                    <Author
                      key={user?.id}
                      link
                      onClick={() => onAddAuthor(user!)}
                      profile={user?.profile}
                      onKeyDown={e => {
                        if (['Enter', ' '].includes(e.key)) {
                          e.preventDefault();
                          onAddAuthor(user!);
                        }
                      }}
                    />
                  ),
              )}
            </AutoCompleteCard>
          )}
          <FillButton onClick={() => setEditing(false)} tw="mt-2">
            Done
          </FillButton>
        </div>
      )}
    </div>
  );
};
